import { collection, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { StatisticsRepository, RoundPodium } from "@/modules/leagues/repositories/StatisticsRepository";
import type { RankingRow, RankingScope, LeagueParticipantStatsDoc } from "@/modules/leagues/types/ranking";

type ParticipantDoc = LeagueParticipantStatsDoc;

interface ScoredPrediction {
  userId: string;
  round: number;
  pointsEarned: number;
  matchDate: Date;
}

function toRows(participants: ParticipantDoc[], pointsFn: (p: ParticipantDoc) => number): RankingRow[] {
  return participants
    .map((p) => ({
      posicao: 0,
      uid: p.uid,
      nome: p.nome,
      avatarUrl: p.avatarUrl,
      pontos: pointsFn(p),
      acertosPlacar: p.acertosPlacarExato ?? 0,
      acertosVencedor: 0,
      percentualAcerto: p.totalPalpitesAvaliados ? Math.round(((p.acertosPlacarExato ?? 0) / p.totalPalpitesAvaliados) * 100) : 0,
      maiorSequencia: 0,
    }))
    .sort((a, b) => b.pontos - a.pontos)
    .map((row, idx) => ({ ...row, posicao: idx + 1 }));
}

/** Mesma forma de `toRows`, mas somando um mapa uid→pontos (usado por
 * Rodada/Mês/Pódios, calculados direto dos palpites — ver comentário na
 * classe). Participante sem nenhum palpite pontuado no recorte pedido
 * ainda aparece, com 0 pts — nunca só "some" da lista. */
function toRowsFromMap(participants: ParticipantDoc[], pointsByUid: Map<string, number>): RankingRow[] {
  return toRows(participants, (p) => pointsByUid.get(p.uid) ?? 0);
}

/**
 * Implementação REAL de StatisticsRepository (Priority 11 + itens 20-22 da
 * revisão final — "ranking real por rodada e mês").
 *
 * "Geral" usa `pontos` (soma corrida, mantida pelo cron a cada palpite
 * pontuado — sempre correta e completa, nunca teve o problema abaixo).
 *
 * "Rodada", "Mês" e "Pódios" foram REESCRITOS pra ler direto da coleção
 * `predictions` (fonte primária, sempre completa) em vez dos mapas-resumo
 * `pontosPorRodada`/`pontosPorMes` em `participants/{uid}` — esses mapas
 * ficaram incompletos pra pontuações antigas gravadas antes de algum
 * ajuste anterior desta integração (o cron nunca reprocessa um palpite já
 * pontuado, então um "buraco" histórico nesses campos nunca se autocorrige
 * sozinho). Calcular direto dos palpites é auto-suficiente: sempre bate
 * com a realidade, não importa quando/como cada palpite foi pontuado.
 */
export class FirestoreStatisticsRepository implements StatisticsRepository {
  async getParticipants(leagueId: string): Promise<ParticipantDoc[]> {
    const snap = await getDocs(collection(db, "groups", leagueId, "participants"));
    return snap.docs.map((d) => d.data() as ParticipantDoc);
  }

  /**
   * Todos os palpites já PONTUADOS da liga. Busca via endpoint no
   * backend (`/api/league/scored-predictions`), NÃO consulta o Firestore
   * direto do navegador — o Firestore recusa esse tipo de busca em massa
   * (múltiplos usuários, cruzando associação à liga) nas suas próprias
   * regras de segurança para "list queries" com OU + consulta cruzada
   * (limitação documentada do Firestore, não falta de ajuste de regra).
   * O endpoint verifica de verdade, com o token de identidade do usuário,
   * que ele é membro daquela liga antes de devolver qualquer dado —
   * mesmo nível de proteção de sempre, só que aplicado no servidor.
   */
  private async fetchScoredPredictions(leagueId: string): Promise<ScoredPrediction[]> {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    let idToken: string;
    try {
      idToken = await currentUser.getIdToken();
    } catch (err) {
      console.error("[FirestoreStatisticsRepository] falha ao obter token de autenticação:", err);
      throw err;
    }

    let res: Response;
    try {
      res = await fetch(`/api/league/scored-predictions?leagueId=${encodeURIComponent(leagueId)}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
    } catch (err) {
      console.error("[FirestoreStatisticsRepository] falha de rede ao buscar palpites pontuados:", err);
      throw err;
    }

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const err = new Error(`Falha ao buscar palpites pontuados: HTTP ${res.status}${body ? ` — ${body}` : ""}`);
      console.error("[FirestoreStatisticsRepository]", err.message);
      throw err;
    }

    const { predictions } = (await res.json()) as {
      predictions: Array<{ userId: string; round: number; pointsEarned: number; matchDate: string | null }>;
    };
    return predictions.map((p) => ({
      userId: p.userId,
      round: p.round,
      pointsEarned: p.pointsEarned,
      matchDate: p.matchDate ? new Date(p.matchDate) : new Date(0),
    }));
  }

  async getRanking(leagueId: string, scope: RankingScope, roundOrMonth?: number | string): Promise<RankingRow[]> {
    if (scope === "geral") {
      const participants = await this.getParticipants(leagueId);
      return toRows(participants, (p) => p.pontos ?? 0);
    }

    const [participants, scored] = await Promise.all([this.getParticipants(leagueId), this.fetchScoredPredictions(leagueId)]);
    const pointsByUid = new Map<string, number>();

    if (scope === "rodada") {
      const round = Number(roundOrMonth);
      for (const s of scored) {
        if (s.round !== round) continue;
        pointsByUid.set(s.userId, (pointsByUid.get(s.userId) ?? 0) + s.pointsEarned);
      }
    } else {
      // mensal
      const month = String(roundOrMonth ?? new Date().toISOString().slice(0, 7));
      for (const s of scored) {
        if (s.matchDate.toISOString().slice(0, 7) !== month) continue;
        pointsByUid.set(s.userId, (pointsByUid.get(s.userId) ?? 0) + s.pointsEarned);
      }
    }

    return toRowsFromMap(participants, pointsByUid);
  }

  async getPodiums(leagueId: string): Promise<RoundPodium[]> {
    const [participants, scored] = await Promise.all([this.getParticipants(leagueId), this.fetchScoredPredictions(leagueId)]);

    const pointsByRoundAndUid = new Map<number, Map<string, number>>();
    for (const s of scored) {
      const byUid = pointsByRoundAndUid.get(s.round) ?? new Map<string, number>();
      byUid.set(s.userId, (byUid.get(s.userId) ?? 0) + s.pointsEarned);
      pointsByRoundAndUid.set(s.round, byUid);
    }

    return Array.from(pointsByRoundAndUid.keys())
      .sort((a, b) => a - b)
      .map((round) => ({
        round,
        top3: toRowsFromMap(participants, pointsByRoundAndUid.get(round)!).slice(0, 3),
      }));
  }

  async getLatestScoredRound(leagueId: string): Promise<number | null> {
    const scored = await this.fetchScoredPredictions(leagueId);
    let latest: number | null = null;
    for (const s of scored) {
      if (latest === null || s.round > latest) latest = s.round;
    }
    return latest;
  }

  /**
   * Placar mais palpitado (item 25). Lê a coleção raiz `predictions`
   * filtrando SEMPRE por `leagueId` (nunca mistura ligas — item 33) e por
   * `participantsVisible == true` (mesma condição que a Firestore Rule de
   * `predictions` exige pra liberar leitura entre participantes — ver
   * `firestore.rules`). Antes usava `matchDate <= agora`; migrado junto
   * com a correção do bug "ninguém palpitou" — as duas precisam usar
   * exatamente a mesma condição da regra, senão volta o mesmo erro de
   * permissão aqui. Requer o índice composto (leagueId,
   * participantsVisible) — se não existir ainda, a query abaixo falha com
   * um link da própria Firebase pra criar automaticamente.
   */
  async getMostPredictedScore(leagueId: string): Promise<{ home: number; away: number; count: number } | null> {
    const q = query(
      collection(db, "predictions"),
      where("leagueId", "==", leagueId),
      where("participantsVisible", "==", true)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const counts = new Map<string, { home: number; away: number; count: number }>();
    for (const doc of snap.docs) {
      const data = doc.data() as { homeScore: number; awayScore: number };
      const key = `${data.homeScore}-${data.awayScore}`;
      const entry = counts.get(key) ?? { home: data.homeScore, away: data.awayScore, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    }

    let best: { home: number; away: number; count: number } | null = null;
    for (const entry of counts.values()) {
      if (!best || entry.count > best.count) best = entry;
    }
    return best;
  }
}
